import {
  afterNextRender,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';

export type RvLinkVariant = 'regular' | 'critical';
export type RvLinkAppearance = 'standalone' | 'inline';

@Component({
  selector: 'a[rv-link], button[rv-link]',
  exportAs: 'rvLink',
  template: '<ng-content />',
  styleUrl: './link.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'rv-link',
    '[class]': 'modifierClasses()',
    '[attr.disabled]': 'isNativeButton && disabled() ? "" : null',
    '[attr.aria-disabled]': '!isNativeButton && disabled() ? "true" : null',
    '[attr.tabindex]': 'tabIndex()',
    '[attr.role]': 'isAction() ? "button" : null',
    '(keydown.enter)': 'activate($event)',
    '(keydown.space)': 'activate($event)',
  },
})
export class RvLinkComponent {
  variant = input<RvLinkVariant>('regular');
  /** `inline` keeps the underline so links inside running text are not identified by color alone. */
  appearance = input<RvLinkAppearance>('standalone');
  disabled = input(false, { transform: booleanAttribute });

  private readonly element =
    inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  protected readonly isNativeButton = this.element.tagName === 'BUTTON';

  /** Static `tabindex` from the template or a directive such as `actionLink`; the host binding would otherwise remove it. */
  private readonly staticTabIndex = this.element.getAttribute('tabindex');

  /**
   * An `<a>` without an `href` after the first render (bindings such as `uiSref` set it late)
   * acts as a button, so it gets the focusability and keyboard activation an `href` would give.
   * Anchors using `actionLink` or an explicit `tabindex` already handle this themselves.
   */
  protected readonly isAction = signal(false);

  protected tabIndex = computed(() => {
    if (this.isNativeButton) {
      return null;
    }
    if (this.disabled()) {
      return '-1';
    }
    return this.staticTabIndex ?? (this.isAction() ? '0' : null);
  });

  protected modifierClasses = computed(() =>
    [
      `rv-link--${this.variant()}`,
      `rv-link--${this.appearance()}`,
      this.disabled() ? 'rv-link--disabled' : '',
    ]
      .filter(Boolean)
      .join(' '),
  );

  constructor() {
    // Capture phase so the block runs before (click) handlers bound on the same host.
    const blockWhenDisabled = (event: Event) => {
      if (this.disabled()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    this.element.addEventListener('click', blockWhenDisabled, true);
    inject(DestroyRef).onDestroy(() =>
      this.element.removeEventListener('click', blockWhenDisabled, true),
    );

    afterNextRender(() => {
      this.isAction.set(
        !this.isNativeButton &&
          this.staticTabIndex === null &&
          !this.element.hasAttribute('href') &&
          !this.element.hasAttribute('actionlink'),
      );
    });
  }

  /** Mirrors native button behavior: Enter and Space both click an action anchor. */
  protected activate(event: Event) {
    if (!this.isAction() || this.disabled()) {
      return;
    }
    event.preventDefault();
    this.element.click();
  }
}
